import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Car,
  Phone,
  User,
  Building2,
  StickyNote,
  Trash2,
  Edit,
  Calendar,
  Clock
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getAgencyColorByAgency, AgencyDot } from "@/lib/agencyColors";

export default function ClientsPage() {
  const { user, agencies } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(user?.agency_id || "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  const [newClient, setNewClient] = useState({
    name: "",
    phone: "",
    vehicle: "",
    immatriculation: "",
    agency_id: user?.agency_id || ""
  });

  useEffect(() => {
    fetchClients();
  }, [selectedAgency]);

  useEffect(() => {
    if (selectedClient) {
      fetchNotes(selectedClient.id);
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const agencyParam = selectedAgency !== "all" ? `?agency_id=${selectedAgency}` : "";
      const res = await axios.get(`${API}/clients${agencyParam}`);
      setClients(res.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async (clientId) => {
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/notes`);
      setNotes(res.data);
    } catch (error) {
      console.error("Failed to fetch notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      if (!newClient.name || !newClient.phone || !newClient.agency_id) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }
      
      await axios.post(`${API}/clients`, newClient);
      toast.success("Client créé avec succès");
      setShowAddDialog(false);
      fetchClients();
      resetNewClient();
    } catch (error) {
      toast.error("Erreur lors de la création du client");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API}/clients/${selectedClient.id}/notes`, {
        content: newNote,
        client_id: selectedClient.id
      });
      toast.success("Note ajoutée");
      setNewNote("");
      fetchNotes(selectedClient.id);
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la note");
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success("Client supprimé");
      fetchClients();
      setShowDetailSheet(false);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetNewClient = () => {
    setNewClient({
      name: "",
      phone: "",
      vehicle: "",
      immatriculation: "",
      agency_id: user?.agency_id || ""
    });
  };

  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(query) ||
      client.vehicle.toLowerCase().includes(query) ||
      client.immatriculation.toLowerCase().includes(query) ||
      client.phone.includes(query)
    );
  });

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency?.city || "—";
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl sm:text-3xl font-bold text-white"
            style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
          >
            CLIENTS / PROSPECTS
          </h1>
          <p className="text-slate-400 mt-1">
            {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} enregistré{filteredClients.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-[200px] bg-slate-900 border-slate-800 text-slate-100 placeholder:text-slate-600"
              data-testid="search-clients"
            />
          </div>

          {user?.role === "admin" && (
            <Select value={selectedAgency} onValueChange={setSelectedAgency}>
              <SelectTrigger className="w-[160px] bg-slate-900 border-slate-800 text-slate-200" data-testid="clients-agency-filter">
                <Building2 className="w-4 h-4 mr-2 text-slate-500" />
                <SelectValue placeholder="Agence" />
              </SelectTrigger>
              <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                <SelectItem value="all" className="text-slate-200">Toutes</SelectItem>
                {agencies.map((agency) => (
                  <SelectItem key={agency.id} value={agency.id} className="text-slate-200">
                    {agency.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Client
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  NOUVEAU CLIENT
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Ajouter un nouveau client ou prospect
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nom complet *</Label>
                  <Input
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="bg-slate-950 border-slate-800 text-slate-100"
                    data-testid="new-client-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Téléphone *</Label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="bg-slate-950 border-slate-800 text-slate-100"
                    data-testid="new-client-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Véhicule</Label>
                  <Input
                    value={newClient.vehicle}
                    onChange={(e) => setNewClient({ ...newClient, vehicle: e.target.value })}
                    placeholder="BMW 320d F30"
                    className="bg-slate-950 border-slate-800 text-slate-100"
                    data-testid="new-client-vehicle"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Immatriculation</Label>
                  <Input
                    value={newClient.immatriculation}
                    onChange={(e) => setNewClient({ ...newClient, immatriculation: e.target.value.toUpperCase() })}
                    placeholder="AB-123-CD"
                    className="bg-slate-950 border-slate-800 text-slate-100 font-mono"
                    data-testid="new-client-immat"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Agence *</Label>
                  <Select 
                    value={newClient.agency_id} 
                    onValueChange={(v) => setNewClient({ ...newClient, agency_id: v })}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="new-client-agency">
                      <SelectValue placeholder="Sélectionner une agence" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id} className="text-slate-200">
                          {agency.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleCreateClient}
                    className="flex-1 btn-primary"
                    data-testid="confirm-new-client-btn"
                  >
                    Créer
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Clients Table */}
      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Client</TableHead>
                <TableHead className="text-slate-400">Véhicule</TableHead>
                <TableHead className="text-slate-400 hidden md:table-cell">Immatriculation</TableHead>
                <TableHead className="text-slate-400 hidden lg:table-cell">Agence</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i} className="border-slate-800">
                    <TableCell colSpan={5}>
                      <div className="h-12 bg-slate-800 rounded skeleton" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredClients.length === 0 ? (
                <TableRow className="border-slate-800">
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun client trouvé</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow 
                    key={client.id} 
                    className="border-slate-800 table-row-hover cursor-pointer"
                    onClick={() => {
                      setSelectedClient(client);
                      setShowDetailSheet(true);
                    }}
                    data-testid={`client-row-${client.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{client.name}</p>
                          <p className="text-sm text-slate-500">{client.phone}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-300">{client.vehicle || "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="font-mono text-slate-400">{client.immatriculation || "—"}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {(() => {
                        const color = getAgencyColorByAgency(agencies.find(a => a.id === client.agency_id));
                        return (
                          <Badge variant="outline" className="gap-1.5" style={{ borderColor: color.border, color: color.text, background: color.bg }}>
                            <AgencyDot color={color} className="w-2 h-2" />
                            {getAgencyName(client.agency_id)}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-slate-400 hover:text-white hover:bg-slate-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClient(client);
                          setShowDetailSheet(true);
                        }}
                        data-testid={`view-client-${client.id}`}
                      >
                        <StickyNote className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Client Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent className="bg-slate-900 border-slate-800 w-full sm:max-w-md overflow-y-auto">
          {selectedClient && (
            <>
              <SheetHeader>
                <SheetTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <User className="w-5 h-5 text-[#FEED00]" />
                  FICHE CLIENT
                </SheetTitle>
                <SheetDescription className="text-slate-400">
                  {selectedClient.name}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Client Info */}
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Téléphone</p>
                      <p className="text-white font-mono">{selectedClient.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Véhicule</p>
                      <p className="text-white">{selectedClient.vehicle || "Non renseigné"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-slate-500 text-xs font-bold">ID</span>
                    <div>
                      <p className="text-sm text-slate-500">Immatriculation</p>
                      <p className="text-white font-mono">{selectedClient.immatriculation || "Non renseigné"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Agence</p>
                      <p className="text-white">{getAgencyName(selectedClient.agency_id)}</p>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Notes ({notes.length})
                  </h3>
                  
                  {/* Add Note */}
                  <div className="flex gap-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Ajouter une note..."
                      className="bg-slate-950 border-slate-800 text-slate-100 resize-none"
                      rows={2}
                      data-testid="new-note-input"
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      className="btn-primary px-3"
                      data-testid="add-note-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Notes List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {loadingNotes ? (
                      <div className="text-center py-4 text-slate-500">Chargement...</div>
                    ) : notes.length === 0 ? (
                      <div className="text-center py-4 text-slate-500 text-sm">
                        Aucune note pour ce client
                      </div>
                    ) : (
                      notes.map((note) => (
                        <div 
                          key={note.id}
                          className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
                          data-testid={`note-${note.id}`}
                        >
                          <p className="text-slate-300 text-sm whitespace-pre-wrap">{note.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                            <span>{note.user_name}</span>
                            <span>•</span>
                            <span>{format(new Date(note.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2 pt-4 border-t border-slate-800">
                  <Button
                    variant="ghost"
                    onClick={() => handleDeleteClient(selectedClient.id)}
                    className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid="delete-client-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer le client
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
